// @/utils/pdfQuotationFixedGenerator.js
import PDFQuotationBase from "./pdfQuotationBase";

/**
 * PDFQuotationFixedGenerator
 * สร้าง docDefinition สำหรับ Quotation แบบ Fixed Price
 * (ตรงกับตัวอย่าง "Quotation / ใบเสนอค่าวิชาชีพกฎหมาย" หน้าแรก — มี Sub Total / VAT / Grand Total)
 *
 * โครงสร้างเดียวกับ PDFQuotationEstimateGenerator (flow-based manual pagination) —
 * ไม่มี absolutePosition/footer-margin สำรองแบบเดิมอีกต่อไป เพราะทดสอบจริงพบว่าเป็น
 * ต้นเหตุของบั๊กหลายอย่าง (หน้าเปล่าแทรก, กล่องสรุปตัดข้ามหน้า, เส้นขอบเยื้อง ฯลฯ)
 */
const DEFAULT_COMPANY_DISPLAY_NAME = "Dej-Udom & Associates Ltd.";
// ✅ เปลี่ยนจาก string ตายตัวเป็นฟังก์ชัน แทรกชื่อบริษัทที่เลือกจริง (company_display_name)
// ให้ตรงกับหัวเอกสาร — ถ้าไม่ได้ส่ง company_display_name มา fallback เป็นชื่อเดิม
// ชื่อบริษัทควรมี "." ต่อท้ายอยู่แล้วถ้ามีคำย่อ (เช่น "Ltd.") เพื่อให้ถูกไวยากรณ์ตอนอยู่
// กลางประโยคด้วย (ดู pdfQuotationEstimateGenerator.js) — ฟังก์ชันนี้เช็คไม่ให้จบประโยค
// ด้วย ".." ซ้อนกัน ถ้าชื่อบริษัทลงท้ายด้วย "." อยู่แล้ว
const buildDefaultTerms = (companyDisplayName) => {
    const name = companyDisplayName || DEFAULT_COMPANY_DISPLAY_NAME;
    const sentenceEnding = name.endsWith(".") ? "" : ".";
    return `This quotation is valid for 30 days from the date of issue unless otherwise stated. The fees quoted are based on the scope of work described herein. Any amendment to the scope of work, additional services requested, or unforeseen requirements may result in additional fees. This quotation is subject to acceptance by the client and any applicable engagement procedures of ${name}${sentenceEnding}`;
};

export default class PDFQuotationFixedGenerator extends PDFQuotationBase {
    /**
     * แถวสรุปยอด (Sub Total / VAT / Grand Total / ตัวอักษร) — ไม่มีการเอาเส้นขอบบนออก
     * (ต่างจาก Estimate ที่มีการขอเอาออกโดยเฉพาะ) จึงเป็นกรอบปิดสมบูรณ์ทั้งก้อน
     * @returns {{rows: Array, heights: Array<number>}}
     */
    buildTotalsRows(header) {
        const showCurrency = header.currency || "THB";
        const rows = [];
        const heights = [];

        const hasVat = header.vat_percent !== undefined && header.vat_percent !== null && header.vat_percent !== "";
        const vatBase = Number(header.vat_base) || 0;
        const noVatBase = Number(header.no_vat_base) || 0;
        // แสดงแถวย่อยเฉพาะเมื่อมีรายการที่ไม่คิด VAT จริง — ถ้าทุกรายการคิด VAT หมด
        // แถวย่อยจะซ้ำซ้อนกับ Sub Total เปล่าๆ
        const showBreakdown = hasVat && noVatBase > 0;

        // ===== หมายเหตุอัตรา VAT — วางเหนือแถว Sub Total =====
        // อธิบายว่า VAT ใช้กับทุกรายการ ยกเว้นรายการที่กำกับว่า "Not subject to VAT"
        // ⚠️ ใช้ 2 cells แยก (ไม่ใช่ colSpan) เพื่อให้เส้นแนวตั้งกลางตารางยังลากต่อเนื่อง
        if (hasVat) {
            rows.push([
                {
                    text: `VAT at ${this.formatNumber(header.vat_percent)}% applies to all items unless marked "Not subject to VAT".`,
                    fontSize: 8,
                    italics: true,
                    margin: [5, 3, 5, 3],
                },
                { text: "", margin: [5, 3, 5, 3] },
            ]);
            heights.push(20);
        }

        rows.push([
            { text: "Sub Total / รวมก่อน VAT", fontSize: 10, margin: [5, 3, 5, 3] },
            {
                text: `${this.formatNumber(header.subtotal)} ${showCurrency}`,
                fontSize: 10,
                alignment: "right",
                margin: [5, 3, 5, 3],
            },
        ]);
        heights.push(24);

        // ===== แถวย่อยแยกยอดที่คิด/ไม่คิด VAT (เยื้องเข้ามาให้เห็นว่าเป็นรายละเอียดของ Sub Total) =====
        if (showBreakdown) {
            rows.push([
                { text: "VATable Amount / ยอดที่คิด VAT", fontSize: 9, margin: [20, 2, 5, 2] },
                {
                    text: `${this.formatNumber(vatBase)} ${showCurrency}`,
                    fontSize: 9,
                    alignment: "right",
                    margin: [5, 2, 5, 2],
                },
            ]);
            heights.push(20);

            rows.push([
                { text: "Not subject to VAT / ยอดที่ไม่คิด VAT", fontSize: 9, margin: [20, 2, 5, 2] },
                {
                    text: `${this.formatNumber(noVatBase)} ${showCurrency}`,
                    fontSize: 9,
                    alignment: "right",
                    margin: [5, 2, 5, 2],
                },
            ]);
            heights.push(20);
        }

        if (hasVat) {
            // ระบุฐานที่ใช้คำนวณ VAT ให้ชัดเจนเมื่อมีรายการที่ไม่คิด VAT ปนอยู่
            const vatLabel = showBreakdown
                ? `VAT ${this.formatNumber(header.vat_percent)}% on ${this.formatNumber(vatBase)} / ภาษีมูลค่าเพิ่ม ${this.formatNumber(header.vat_percent)}%`
                : `Value Added Tax / ภาษีมูลค่าเพิ่ม (${this.formatNumber(header.vat_percent)}%)`;

            rows.push([
                { text: vatLabel, fontSize: 10, margin: [5, 3, 5, 3] },
                {
                    text: `${this.formatNumber(header.vat_amount)} ${showCurrency}`,
                    fontSize: 10,
                    alignment: "right",
                    margin: [5, 3, 5, 3],
                },
            ]);
            heights.push(24);
        }

        rows.push([
            {
                text: "Grand Total / ยอดรวม",
                fontSize: 11,
                bold: true,
                fillColor: "#eeeeee",
                margin: [5, 5, 5, 3],
            },
            {
                text: `${this.formatNumber(header.grand_total)} ${showCurrency}`,
                fontSize: 11,
                bold: true,
                alignment: "right",
                fillColor: "#eeeeee",
                margin: [5, 5, 5, 3],
            },
        ]);
        heights.push(28);

        if (header.amount_in_words) {
            rows.push([
                {
                    text: `(${header.amount_in_words}) ${showCurrency}`,
                    fontSize: 10,
                    alignment: "center",
                    fillColor: "#eeeeee",
                    margin: [5, 5, 5, 3],
                    colSpan: 2,
                },
                {},
            ]);
            heights.push(26);
        }

        return { rows, heights };
    }

    generateDocDefinition(quotationData) {
        const rawHeader = quotationData?.header || {};
        const items = quotationData?.items || [];
        const header = this.buildCompanyHeader(rawHeader);

        const docTitle = "Quotation / ใบเสนอค่าวิชาชีพกฎหมาย";
        header.doc_title_en = "Quotation No";
        header.doc_title_th = "เลขที่ใบเสนอราคา";
        header.doc_no = rawHeader.doc_no;
        header.doc_date = rawHeader.doc_date;
        header.subtotal = rawHeader.subtotal;
        header.vat_percent = rawHeader.vat_percent;
        header.vat_base = rawHeader.vat_base;
        header.no_vat_base = rawHeader.no_vat_base;
        header.vat_amount = rawHeader.vat_amount;
        header.grand_total = rawHeader.grand_total;
        header.amount_in_words = rawHeader.amount_in_words;
        header.currency = rawHeader.currency;

        const COL1_WIDTH = 423;
        const COL2_WIDTH = 120;

        // ===== หัวตาราง (แถวเดียว ไม่มี Fee Basis แบบ Estimate) =====
        const headerTable = {
            unbreakable: true,
            table: {
                widths: [COL1_WIDTH, COL2_WIDTH],
                body: [
                    [
                        { text: "Description / รายการ", fontSize: 10, bold: true, margin: [5, 5, 5, 5] },
                        { text: "Amount / จำนวนเงิน", fontSize: 10, bold: true, alignment: "right", margin: [5, 5, 5, 5] },
                    ],
                ],
            },
            layout: {
                // เส้นบนสุดของกรอบ + เส้นใต้หัวตาราง (ทำหน้าที่เป็นเส้นคั่นระหว่าง header
                // กับตารางรายการไปในตัว ไม่ต้องมีเส้นแยกต่างหากแบบ feeBasisDivider ของ Estimate)
                hLineWidth: (i) => (i === 0 || i === 1 ? 1 : 0),
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };

        // ===== ตารางสรุปยอด: Sub Total / VAT / Grand Total / ตัวอักษร =====
        // มีเส้นขอบบนเต็ม (ต่างจาก Estimate ที่ขอเอาเส้นบนของ note ออกโดยเฉพาะ) จึงเป็น
        // กรอบปิดสมบูรณ์ทั้งก้อน — unbreakable กันแถวเหล่านี้ถูกตัดข้ามหน้า
        const { rows: totalsRows, heights: totalsHeights } = this.buildTotalsRows(header);
        const totalsBlockHeight = totalsHeights.reduce((s, h) => s + h, 0);

        const totalsTable = {
            unbreakable: true,
            table: {
                widths: [COL1_WIDTH, COL2_WIDTH],
                body: totalsRows,
                heights: totalsHeights,
            },
            layout: {
                // ไม่วาดเส้นบนสุด (i === 0) เพราะแถวแรกคือหมายเหตุ VAT ที่ต่อเนื่องมาจาก
                // ตารางรายการด้านบนอยู่แล้ว — เส้นซ้ำจะกลายเป็นเส้นคู่ตรงรอยต่อ
                hLineWidth: (i) => (i === 0 ? 0 : 1),
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };

        // ===== Quotation Terms — ก้อนอิสระ unbreakable ของตัวเอง (แยกจาก totalsTable) =====
        const terms = rawHeader.quotation_terms || buildDefaultTerms(rawHeader.company_display_name);
        const termsBlockContent = this.generateTermsBlock(terms);
        termsBlockContent.unbreakable = true;
        const TERMS_HEIGHT_ESTIMATE = this.estimateTermsHeight(terms);

        // ⚠️ แบ่งรายการเป็น "หน้า" เอง ล่วงหน้า เหมือนกับ Estimate Generator ทุกประการ —
        // ดูคอมเมนต์ละเอียดใน pdfQuotationEstimateGenerator.js (root cause + วิธีแก้)
        const PAGE_HEIGHT = 841.89; // A4 (pt)
        const TOP_MARGIN = 25;
        const BOTTOM_MARGIN = 25;
        const COMPANY_HEADER_HEIGHT = 80;
        const TITLE_HEIGHT = 30;
        const DIVIDER_HEIGHT = 11;
        const CLIENT_INFO_HEIGHT = this.estimateClientInfoHeight(header);
        const SAFETY_BUFFER = 6;
        const HEADER_TABLE_HEIGHT = 26; // แถวหัวตารางแถวเดียว (ไม่มี Fee Basis)
        const PAGE_SAFETY_MARGIN = 35;

        const page1PrefixHeight =
            TOP_MARGIN +
            COMPANY_HEADER_HEIGHT +
            TITLE_HEIGHT +
            DIVIDER_HEIGHT +
            CLIENT_INFO_HEIGHT +
            SAFETY_BUFFER +
            HEADER_TABLE_HEIGHT;
        const page1Available = PAGE_HEIGHT - page1PrefixHeight - BOTTOM_MARGIN - PAGE_SAFETY_MARGIN;
        const continuationAvailable = PAGE_HEIGHT - TOP_MARGIN - BOTTOM_MARGIN - PAGE_SAFETY_MARGIN;

        const itemRows = this.buildItemRows(items);
        const rowHeights = this.buildRowHeights(items, COL1_WIDTH);

        const pageGroups = [];
        let startIdx = 0;
        let currentHeight = 0;
        let available = page1Available;
        for (let idx = 0; idx < items.length; idx++) {
            const h = rowHeights[idx];
            if (idx > startIdx && currentHeight + h > available) {
                pageGroups.push({ startIdx, endIdx: idx, height: currentHeight, available });
                startIdx = idx;
                currentHeight = 0;
                available = continuationAvailable;
            }
            currentHeight += h;
        }
        pageGroups.push({ startIdx, endIdx: items.length, height: currentHeight, available });

        // เช็คว่า totalsTable (unbreakable) จะพอดีกับพื้นที่ว่างจริงของหน้าที่กะไว้ว่าเป็น
        // "หน้าสุดท้าย" หรือไม่ — ถ้าไม่พอ เพิ่มหน้าว่างใหม่สำหรับ totalsTable โดยเฉพาะ
        const lastGroup = pageGroups[pageGroups.length - 1];
        if (lastGroup.available - lastGroup.height < totalsBlockHeight) {
            pageGroups.push({
                startIdx: lastGroup.endIdx,
                endIdx: lastGroup.endIdx,
                height: 0,
                available: continuationAvailable,
            });
        }

        const itemPageTables = pageGroups.map((pg, idx) => {
            const isFirstGroup = idx === 0;
            const isLastGroup = idx === pageGroups.length - 1;
            const pageRows = itemRows.slice(pg.startIdx, pg.endIdx);
            const pageHeights = rowHeights.slice(pg.startIdx, pg.endIdx);
            // หน้าสุดท้าย: เช็คว่าที่ว่างพอสำหรับทั้ง totalsTable และ Terms ไหม
            //   - พอทั้งคู่ -> เติมที่ว่างเผื่อทั้งคู่ ดันลงไปติดขอบล่างด้วยกัน
            //   - พอแค่ totalsTable -> เติมที่ว่างเผื่อแค่ totalsTable ส่วน Terms ล้นไปเอง
            // หน้าอื่นๆ เติมที่ว่างให้เต็มพื้นที่ตามปกติ
            let fillerHeight;
            if (isLastGroup) {
                const slack = pg.available - pg.height;
                const bothFit = slack - totalsBlockHeight >= TERMS_HEIGHT_ESTIMATE;
                fillerHeight = bothFit
                    ? slack - totalsBlockHeight - TERMS_HEIGHT_ESTIMATE
                    : Math.max(slack - totalsBlockHeight, 0);
            } else {
                fillerHeight = Math.max(pg.available - pg.height, 0);
            }

            const table = {
                table: {
                    widths: [COL1_WIDTH, COL2_WIDTH],
                    body: [
                        ...pageRows,
                        [
                            { text: "", margin: [5, 3, 5, 3] },
                            { text: "", margin: [5, 3, 5, 3] },
                        ],
                    ],
                    heights: (i) => (i === pageRows.length ? fillerHeight : "auto"),
                },
                layout: {
                    // หน้าแรกไม่ต้องมีเส้นบน (ต่อจาก headerTable แบบไร้รอยต่อ)
                    // หน้าอื่นๆ (ขึ้นหน้าใหม่) มีเส้นบนปิดกรอบให้เห็นชัดว่าเริ่มกล่องใหม่
                    // หน้าที่ไม่ใช่หน้าสุดท้าย ปิดเส้นขอบล่างให้สมบูรณ์
                    // หน้าสุดท้ายเปิดขอบล่างไว้ ต่อกับ totalsTable แบบไร้รอยต่อ
                    hLineWidth: (i, node) => {
                        if (i === 0) return isFirstGroup ? 0 : 1;
                        if (i === node.table.body.length) return isLastGroup ? 0 : 1;
                        return 0;
                    },
                    vLineWidth: () => 1,
                    hLineColor: () => "black",
                    vLineColor: () => "black",
                    paddingLeft: () => 0,
                    paddingRight: () => 0,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                },
            };
            if (!isFirstGroup) table.pageBreak = "before";
            return table;
        });

        const additionalInfoBlock = this.generateAdditionalInfoBlock(rawHeader.additional_info);

        const content = [
            this.generateCompanyHeaderBlock(header),
            this.generateTitleBlock(header, docTitle),
            this.generateDividerLine(),
            this.generateClientInfoBlock(header),
            headerTable,
            ...itemPageTables,
            totalsTable,
            termsBlockContent,
        ];

        // ✅ Additional Information — ขึ้นหน้าใหม่ต่อจากหน้าสุดท้ายเสมอ (ถ้ามีข้อความ)
        if (additionalInfoBlock) content.push(additionalInfoBlock);

        return {
            pageSize: "A4",
            pageMargins: [TOP_MARGIN, TOP_MARGIN, TOP_MARGIN, BOTTOM_MARGIN],
            content,
            defaultStyle: {
                font: "Sarabun",
                fontSize: 10,
            },
        };
    }
}